import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, uploadFile, ocrScan } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { compressImage } from "@/lib/image";
import { formatCZK, formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useIsMobile } from "@/components/shared/PagePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignedImg } from "@/components/shared/SignedImg";
import { DirectionHint } from "@/components/shared/DocumentReviewDialog";
import { NotInvoiceHint } from "@/components/shared/NotInvoiceHint";
import { DuplicateBanner, scrollToDuplicateBanner } from "@/components/shared/DuplicateBanner";
import { NOT_ACCOUNTING, kindBlocked, notAccountingFrom409, applyDocType, issuedPayloadFromDoc, receivedPayloadFromDoc, receiptPayload, DUP_CHECK_URL, duplicatesFrom409, invoiceFromOcr, receiptFromInvoiceOcr, receiptFromDocState, ocrCurrency } from "@/lib/documents";
import { DocCurrency } from "@/components/shared/DocCurrency";
import { pendingCurrency } from "@/lib/documents";
import { KindSwitchedHint } from "@/components/shared/DocumentReviewDialog";
import { CreditNoteFields } from "@/components/shared/CreditNoteFields";
import { AdvancesSection, TaxDocPicker } from "@/components/shared/AdvancesSection";
import { DirectionToggle, DocKindRow } from "@/components/shared/DocKind";
import { NumInput } from "@/components/shared/NumInput";
import { FileText, ReceiptText, CheckCircle2, X, CreditCard, MapPin } from "lucide-react";
import { useDocPicker } from "@/components/shared/DocPicker";
import { toast } from "sonner";

// 336: PDF dostane čtečku s pevnou výškou; `max-h-52` platí jen pro fotku (čtečka by přes něj přetekla do formuláře).
const PHOTO_CLS = "w-full max-h-52 object-contain rounded-md border border-border";
const fileCls = (u) => ((u || "").toLowerCase().includes(".pdf") ? "w-full" : PHOTO_CLS);
const PDF_FRAME = "h-[320px] md:h-[380px]";

const CATS_FALLBACK = ["Materiál", "Subdodávka", "Doprava", "Nářadí", "Ostatní"];
const RCATS_FALLBACK = ["PHM (pohonné hmoty)", "Kancelářské vybavení", "Ubytování (služební cesty)", "Cestovné a diety", "Stravné a reprezentace"];
const normalizePay = (v) => {
  const s = (v || "").toLowerCase();
  if (s.includes("kart") || s.includes("card")) return "Kartou";
  if (s.includes("hotov") || s.includes("cash")) return "Hotovost";
  return v || "";
};

// Full-screen upload flow (OCR -> review -> save) shown as an overlay.
// 184: soubor vybírá volající jedním klepnutím (useDocPicker) a předá ho v `file`;
// krok „source" zbývá jen jako „Vybrat znovu", když se doklad nepodařilo načíst.
export function DocUploadOverlay({ type, file = null, onClose, onSaved }) {
  const { company } = useAuth();
  const cats = company?.cost_categories?.length ? company.cost_categories : CATS_FALLBACK;
  const rcats = company?.receipt_categories?.length ? company.receipt_categories : RCATS_FALLBACK;
  const open = !!type;
  const isInvoice = type === "faktura";
  const isMobile = useIsMobile();

  const [projects, setProjects] = useState([]);
  const [step, setStep] = useState("source"); // source | ocr | review
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);
  const [rec, setRec] = useState(null);
  // Duplicity (149): `dups` plní kontrola po OCR / při úpravách i odmítnutí 409.
  // Jistá shoda (153) = uložit nejde: patička nabídne jen Zahodit / Zobrazit existující.
  const [dups, setDups] = useState([]);
  const lastDupKey = useRef("");
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const retry = useDocPicker({ onPick: (f) => processFile(f) });

  useEffect(() => {
    if (open) {
      setStep(file ? "ocr" : "source"); setDoc(null); setRec(null); setDups([]); setFailed(false); lastDupKey.current = "";
      api.get("/projects/options").then((r) => setProjects(r.data)).catch(() => {});
      if (file) processFile(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, file]);

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const setLine = (i, k, v) => setDoc((d) => ({ ...d, line_items: d.line_items.map((li, idx) => idx === i ? { ...li, [k]: v } : li) }));
  const applyAllRemaining = (pid) => setDoc((d) => ({ ...d, line_items: d.line_items.map((li) => li.project_id ? li : { ...li, project_id: pid }) }));

  const docKind = (d) => (d?.ui_type === "vydana" ? "issued" : "document");
  const docPayload = (d) => (d.ui_type === "vydana" ? issuedPayloadFromDoc(d) : receivedPayloadFromDoc(d));
  const checkDups = async (kind, payload) => {
    const key = kind + JSON.stringify(payload);
    if (key === lastDupKey.current) return;
    lastDupKey.current = key;
    try { const { data } = await api.post(DUP_CHECK_URL[kind], payload); setDups(data.duplicates || []); } catch { /* kontrola je jen nápověda */ }
  };
  // Znovu zkontrolovat (600 ms po poslední změně), když se změní strana / číslo / částka / datum.
  // Kontrola účtenky běží, kdykoli je `rec` (i účtenka rozpoznaná přes „Nahrát fakturu", 150).
  useEffect(() => {
    if (step !== "review" || !doc || rec) return;
    const t = setTimeout(() => checkDups(docKind(doc), docPayload(doc)), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, !!rec, doc?.ui_type, doc?.supplier_name, doc?.supplier_ico, doc?.invoice_number, doc?.variable_symbol, doc?.total_with_vat, doc?.issue_date]);
  useEffect(() => {
    if (step !== "review" || !rec) return;
    const t = setTimeout(() => checkDups("receipt", receiptPayload(rec)), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rec?.supplier_name, rec?.receipt_number, rec?.total, rec?.date]);
  const showDup = (d) => { onClose(); navigate(`/ekonomika/doklady?open=${d.kind}:${d.id}`); };
  // Ruční „Typ → Účtenka" u skenu faktury / „Otevřít jako fakturu" z účtenky (150).
  const becomeReceipt = () => { const next = receiptFromDocState(doc); setDoc(null); setRec(next); setDups([]); lastDupKey.current = ""; checkDups("receipt", receiptPayload(next)); };
  const openAsInvoice = () => { const next = invoiceFromOcr(rec.ocr_raw || {}, rec.file_url, cats, company?.currency); setRec(null); setDoc(next); setDups([]); lastDupKey.current = ""; checkDups(docKind(next), docPayload(next)); };

  const processFile = async (f) => {
    if (!f) return;
    setStep("ocr"); setBusy(true); setFailed(false);
    try {
      if (isInvoice) {
        const url = await uploadFile(f);
        const data = await ocrScan("document", url);
        if (data.doc_type === "uctenka" && !NOT_ACCOUNTING[data.doc_kind]) {
          // Účtenka přes „Nahrát fakturu" (150) → kontrola účtenky, overlay přepne sám.
          // 386: objednávka nebo dodací list zůstane v okně faktury, kde je vidět upozornění.
          const next = receiptFromInvoiceOcr(data, url);
          setRec(next); checkDups("receipt", receiptPayload(next));
        } else {
          // Typ předvyplní OCR podle IČO/DIČ/názvu firmy (zadání 148) — supplier_* nese protistranu.
          const next = invoiceFromOcr(data, url, cats, company?.currency);
          setDoc(next); checkDups(docKind(next), docPayload(next));
        }
      } else {
        const url = await uploadFile(await compressImage(f));
        const data = await ocrScan("receipt", url);
        const items = (data.items || []).map((it) => ({ description: it.description || "", quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 }));
        const next = { file_url: url, supplier_name: data.supplier_name || "", address: data.address || "", receipt_number: data.receipt_number || "", date: (data.date || "").slice(0, 10), total: Number(data.total) || 0, vat_amount: Number(data.vat_amount) || 0, payment_method: normalizePay(data.payment_method), category: "", project_id: null, items, currency: ocrCurrency(data.currency) };
        setRec(next);
        checkDups("receipt", receiptPayload(next));
      }
      setStep("review");
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Údaje se nepodařilo načíst. Zkuste znovu." : (formatApiError(err.response?.data?.detail) || "Nahrání selhalo."));
      setFailed(true); setStep("source");
    } finally { setBusy(false); }
  };

  const saveDoc = async () => {
    setBusy(true);
    try {
      if (doc.ui_type === "vydana") {
        const payload = issuedPayloadFromDoc(doc);
        if (!payload.invoice_number.trim() || !payload.client_name.trim()) {
          toast.error("Číslo faktury a odběratel jsou povinné.");
          return;
        }
        const { data } = await api.post("/issued-invoices", payload);
        toast.success(data?.tax_doc_attached ? `Doklad k platbě připojen k záloze č. ${data.invoice_number || "—"}.` : "Vydaná faktura uložena.");
      } else {
        const { data } = await api.post("/documents", receivedPayloadFromDoc(doc));   // 386: neúčetní papír bez potvrzení → 409
        toast.success(data?.tax_doc_attached ? `Doklad k platbě připojen k záloze č. ${data.invoice_number || "—"}.`
          : data?.internal_no ? `Doklad uložen jako #${data.internal_no}.` : "Doklad uložen.");
      }
      onSaved?.(); onClose();
    } catch (e) {
      const d = duplicatesFrom409(e);
      if (d) { setDups(d); scrollToDuplicateBanner(); return; }   // banner místo toastu (149)
      const na = notAccountingFrom409(e);                           // 386: server poznal objednávku / dodací list
      if (na) { setDoc((x) => ({ ...x, doc_kind: na.kind, doc_title: na.title, confirm_kind: false })); toast.error(na.message); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  const saveRec = async () => {
    if (!rec.category) return toast.error("Vyberte kategorii výdaje.");
    setBusy(true);
    try {
      await api.post("/receipts", receiptPayload(rec));
      toast.success("Účtenka uložena."); onSaved?.(); onClose();
    } catch (e) {
      const d = duplicatesFrom409(e);
      if (d) { setDups(d); scrollToDuplicateBanner(); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
    finally { setBusy(false); }
  };
  const blocked = dups.some((d) => d.certain);

  // Mobil: spodní sheet s výškou podle obsahu (krok „zdroj" je nízký, kontrola
  // dokladu roluje do max 88vh). Desktop: malý vycentrovaný dialog, ne celá
  // obrazovka. Vnitřek je sdílený — SheetTitle je Radix Dialog.Title, funguje v obou.
  const Wrap = isMobile ? Sheet : Dialog;
  const WrapContent = isMobile ? SheetContent : DialogContent;
  const wrapProps = isMobile
    ? { side: "bottom", className: "h-auto max-h-[88vh] p-0 flex flex-col rounded-t-[24px] [&>button]:hidden" }
    : { className: "w-[560px] max-w-[calc(100vw-2rem)] max-h-[85vh] p-0 gap-0 flex flex-col rounded-[20px] [&>button]:hidden" };

  return (
    <Wrap open={open} onOpenChange={(o) => !o && onClose()}>
      <WrapContent {...wrapProps} data-testid="doc-upload-overlay">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <SheetTitle className="font-bold text-lg">{isInvoice && !rec ? "Nahrát fakturu" : "Nahrát účtenku"}</SheetTitle>
          <SheetDescription className="sr-only">Nahrání a kontrola dokladu</SheetDescription>
          <button onClick={onClose} data-testid="doc-upload-close" className="p-2 -mr-2 text-muted-foreground hover:text-foreground"><X size={22} /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {step === "source" && (
            <div className="flex flex-col items-center text-center gap-4 py-10" data-testid="upload-retry">
              {retry.inputs}
              {failed && <p className="text-sm text-muted-foreground max-w-xs">Doklad se nepodařilo načíst. Zkuste ho vyfotit znovu — rovně a celý v záběru.</p>}
              <button type="button" onClick={() => retry.open(isInvoice ? "faktura" : "uctenka")} data-testid="source-pick"
                className="w-full max-w-xs flex flex-col items-center gap-2 rounded-[20px] border-[1.5px] border-dashed border-brand/70 bg-brand/[0.05] p-6 text-brand font-extrabold hover:bg-brand/10 transition-colors">
                {isInvoice ? <FileText size={30} /> : <ReceiptText size={30} />}
                {failed ? "Vybrat znovu" : isInvoice ? "Vybrat fakturu" : "Vybrat účtenku"}
              </button>
              <Button variant="ghost" onClick={onClose} className="w-full md:w-auto h-11" data-testid="source-cancel">Zrušit</Button>
            </div>
          )}

          {step === "ocr" && (
            <div className="flex flex-col items-center justify-center gap-4 py-16 text-center" data-testid="upload-ocr">
              <div className="w-12 h-12 rounded-full border-4 border-secondary border-t-brand animate-spin motion-reduce:animate-none" />
              <div className="font-semibold">Načítám doklad…</div>
              <div className="text-sm text-muted-foreground">OCR čte položky z dokladu</div>
            </div>
          )}

          {step === "review" && doc && !rec && (() => {
            const issued = doc.ui_type === "vydana";
            return (
            <div className="space-y-3" data-testid="invoice-review">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand bg-brand/10 rounded-md px-2 py-1"><FileText size={13} /> {issued ? (doc.is_credit_note ? "Dobropis vydaný" : "Faktura vydaná") : (doc.is_credit_note ? "Dobropis přijatý" : "Faktura")}</span>
              <DuplicateBanner dups={dups} onShow={showDup} />
              <NotInvoiceHint doc={doc} setDoc={setDoc} />
              {doc.file_url && <SignedImg url={doc.file_url} alt="doklad" className={fileCls(doc.file_url)} frameClass={PDF_FRAME} testid="doc-image" />}
              {/* Typ předvyplní OCR (zadání 148); ruční přepnutí u nového skenu prohodí protistranu; dobropis (167b/C) */}
              {/* 291: kdo doklad vystavil + malý výběr druhu (dobropis je druh, ne směr) */}
              <div className="space-y-2">
                <DirectionToggle issued={issued}
                  onChange={(base) => setDoc((d) => applyDocType(d, d.is_credit_note ? (base === "vydana" ? "dobropis_vydany" : "dobropis_prijaty") : base))}
                  onReceipt={doc.file_url ? becomeReceipt : null} />
                <DirectionHint doc={doc} />
                <DocKindRow doc={doc} setDoc={setDoc} />
                {!issued && <AdvancesSection doc={doc} setDoc={setDoc} />}
                <TaxDocPicker doc={doc} setDoc={setDoc} />
              </div>
              <CreditNoteFields doc={doc} setDoc={setDoc} issued={issued} />
              <div><Label>{issued ? "Odběratel" : "Dodavatel"}</Label><Input data-testid="doc-supplier" value={doc.supplier_name || ""} onChange={(e) => set("supplier_name", e.target.value)} className="h-10 mt-1" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Číslo dokladu</Label><Input data-testid="doc-number" value={doc.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} className="h-10 mt-1" /></div>
                {!issued && (
                <div><Label>Stav platby</Label>
                  <Select value={doc.payment_status} onValueChange={(v) => set("payment_status", v)}><SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Nezaplaceno">Nezaplaceno</SelectItem><SelectItem value="Zaplaceno">Zaplaceno</SelectItem></SelectContent></Select></div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="min-w-0"><Label>Vystaveno</Label><Input type="date" value={(doc.issue_date || "").slice(0, 10)} onChange={(e) => set("issue_date", e.target.value)} className="h-10 mt-1" /></div>
                <div className="min-w-0"><Label>Splatnost</Label><Input type="date" value={(doc.due_date || "").slice(0, 10)} onChange={(e) => set("due_date", e.target.value)} className="h-10 mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Bez DPH</Label><NumInput value={doc.total_without_vat} onChange={(v) => set("total_without_vat", v)} className="h-10 mt-1" /></div>
                <div><Label>DPH</Label><NumInput value={doc.vat_amount} onChange={(v) => set("vat_amount", v)} className="h-10 mt-1" /></div>
              </div>
              {issued ? (
                <div className="grid grid-cols-2 gap-2" data-testid="issued-fields">
                  <div className="col-span-2"><Label>Zakázka</Label>
                    <Select value={doc.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? null : v)}>
                      <SelectTrigger className="h-10 mt-1 w-full" data-testid="issued-project"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Bez zakázky</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                  <AdvancesSection doc={doc} setDoc={setDoc} className="col-span-2" />
                </div>
              ) : (
              <>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => set("assignment_mode", "whole")} data-testid="mode-whole" className={`px-3 py-2.5 text-sm font-bold rounded-lg border text-center ${doc.assignment_mode === "whole" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>Celý na 1 zakázku</button>
                <button onClick={() => set("assignment_mode", "split")} data-testid="mode-split" className={`px-3 py-2.5 text-sm font-bold rounded-lg border text-center ${doc.assignment_mode === "split" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>Rozdělit po položkách</button>
              </div>
              {doc.assignment_mode === "whole" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Zakázka</Label>
                    <Select value={doc.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? null : v)}><SelectTrigger className="h-10 mt-1" data-testid="whole-project"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Režie (bez zakázky)</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Kategorie</Label>
                    <Select value={doc.category || cats[0]} onValueChange={(v) => set("category", v)}><SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                </div>
              ) : (
                <div className="space-y-2" data-testid="split-lines">
                  {doc.line_items.map((li, i) => (
                    <div key={i} className="border border-border rounded-md p-2 space-y-2" data-testid={`line-${i}`}>
                      <Input value={li.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} className="h-9" placeholder="Popis" />
                      <div className="grid grid-cols-2 gap-2">
                        <NumInput value={li.line_total} onChange={(v) => setLine(i, "line_total", v)} className="h-9" placeholder="Celkem" />
                        <Select value={li.category || cats[0]} onValueChange={(v) => setLine(i, "category", v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={li.project_id || "none"} onValueChange={(v) => setLine(i, "project_id", v === "none" ? null : v)}><SelectTrigger className="h-9" data-testid={`line-project-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Režie</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
                        <Select value={li.stock_status || "direct"} onValueChange={(v) => setLine(i, "stock_status", v)}><SelectTrigger className="h-9" data-testid={`line-stock-${i}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="direct">Přímá spotřeba</SelectItem><SelectItem value="stock">Naskladnit</SelectItem></SelectContent></Select>
                      </div>
                    </div>
                  ))}
                  {projects.length > 0 && (
                    <div className="flex items-center gap-2 text-sm"><span className="text-muted-foreground shrink-0">Zbylé na:</span>
                      <Select onValueChange={applyAllRemaining}><SelectTrigger className="h-9" data-testid="apply-remaining"><SelectValue placeholder="Zakázka" /></SelectTrigger><SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                  )}
                </div>
              )}
              </>
              )}
              <div className="flex items-center justify-between font-bold pt-1"><span>Celkem s DPH</span>
                <NumInput data-testid="doc-total" value={doc.total_with_vat} onChange={(v) => set("total_with_vat", v)} className="h-10 w-36 text-right" /></div>
              <DocCurrency doc={doc} setDoc={setDoc} />
            </div>
            );
          })()}

          {step === "review" && rec && (
            <div className="space-y-3" data-testid="receipt-review">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-2 py-1"><ReceiptText size={13} /> Účtenka</span>
              <KindSwitchedHint rec={rec} onOpenAsInvoice={openAsInvoice} />
              <DuplicateBanner dups={dups} onShow={showDup} />
              {rec.file_url && <SignedImg url={rec.file_url} alt="účtenka" className={fileCls(rec.file_url)} frameClass={PDF_FRAME} testid="receipt-image" />}
              <RField label="Obchod" value={rec.supplier_name || "—"} icon={ReceiptText} />
              <div className="grid grid-cols-2 gap-2">
                <RField label="Datum" value={formatDate(rec.date)} />
                <div className="min-w-0"><Label className="text-xs text-muted-foreground">Číslo dokladu</Label>
                  <Input value={rec.receipt_number || ""} onChange={(e) => setRec({ ...rec, receipt_number: e.target.value })} className="h-10 mt-1 w-full min-w-0" placeholder="č. účtenky" data-testid="receipt-number" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <RField label="Celkem s DPH" value={formatMoney(rec.total, pendingCurrency(rec))} strong />
                <div className="min-w-0"><Label className="text-xs text-muted-foreground">DPH</Label>
                  <NumInput value={rec.vat_amount} onChange={(v) => setRec({ ...rec, vat_amount: v })} className="h-10 mt-1 w-full min-w-0" data-testid="receipt-vat-input" /></div>
              </div>
              <DocCurrency doc={rec} setDoc={setRec} totalKey="total" dateKey="date" kind="Účtenka" />
              {Number(rec.total) > 0 && !Number(rec.vat_amount) && (
                <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="receipt-vat-unknown">DPH neuvedeno — účtenka se započítá celá.</p>
              )}
              <div><Label className="flex items-center gap-1.5"><CreditCard size={13} /> Platba</Label>
                <Select value={rec.payment_method || undefined} onValueChange={(v) => setRec({ ...rec, payment_method: v })}><SelectTrigger className="h-10 mt-1" data-testid="receipt-payment"><SelectValue placeholder="Vyberte" /></SelectTrigger>
                  <SelectContent><SelectItem value="Hotovost">Hotovost</SelectItem><SelectItem value="Kartou">Kartou</SelectItem>{rec.payment_method && !["Hotovost", "Kartou"].includes(rec.payment_method) && <SelectItem value={rec.payment_method}>{rec.payment_method}</SelectItem>}</SelectContent></Select></div>
              <div><Label>Kategorie výdaje <span className="text-destructive">*</span></Label>
                <Select value={rec.category || undefined} onValueChange={(v) => setRec({ ...rec, category: v })}><SelectTrigger className="h-10 mt-1" data-testid="receipt-category"><SelectValue placeholder="Vyberte kategorii" /></SelectTrigger>
                  <SelectContent>{rcats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Zakázka</Label>
                <Select value={rec.project_id || "none"} onValueChange={(v) => setRec({ ...rec, project_id: v === "none" ? null : v })}><SelectTrigger className="h-10 mt-1" data-testid="receipt-project"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Režie (bez zakázky)</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              {(rec.items || []).length > 0 && (
                <div><Label>Položky (z účtenky)</Label>
                  <div className="mt-1 border border-border rounded-md divide-y divide-border" data-testid="receipt-items">
                    {rec.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-2.5 text-sm"><span className="min-w-0 truncate">{it.description || "Položka"}</span>
                        <span className="text-muted-foreground shrink-0 tabular-nums">{it.quantity ? `${Number(it.quantity).toLocaleString("cs-CZ")} ×` : ""} {formatCZK(it.unit_price)}</span></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {step === "review" && (
          <div className="shrink-0 border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end bg-background">
            <Button variant="ghost" onClick={isInvoice && !rec ? onClose : () => { setRec(null); setDoc(null); setStep("source"); }} data-testid="review-discard" className="w-full sm:w-auto h-12 font-bold">{isInvoice && !rec ? "Zahodit" : "Smazat / zkusit znovu"}</Button>
            {/* Jistá duplicita (153): místo Uložit jen „Zobrazit existující" */}
            {blocked ? (
              <Button variant="outline" onClick={() => showDup(dups[0])} data-testid="review-show-existing" className="w-full sm:w-auto h-12 font-bold px-6">Zobrazit existující</Button>
            ) : (
              <Button onClick={() => (rec ? saveRec() : saveDoc())} disabled={busy || (!rec && kindBlocked(doc))} data-testid="review-save" className="w-full sm:w-auto h-12 font-bold px-6">
                <CheckCircle2 size={18} /> {rec ? "Uložit účtenku" : doc?.ui_type === "vydana" ? "Uložit vydanou fakturu" : "Uložit doklad"}
              </Button>
            )}
          </div>
        )}
      </WrapContent>
    </Wrap>
  );
}

function RField({ label, value, icon: Icon, strong }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className={`mt-1 flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 h-10 ${strong ? "font-bold" : "text-sm"}`}>
        {Icon && <Icon size={14} className="text-muted-foreground shrink-0" />}<span className="truncate">{value}</span>
      </div>
    </div>
  );
}
